import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

/**
 * Hook that provides preference-aware task creation routing.
 *
 * On first use (no preference saved), opens a preference dialog.
 * After that, routes to either the guided TaskHelper or the create-task form page.
 *
 * @param {string} from - The page the user is coming from (e.g. "dashboard", "tasks", "planner")
 * @param {Function} openGuidedCreator - Callback to open the guided TaskHelperDialog
 */
const useCreateTaskAction = (from, openGuidedCreator) => {
  const navigate = useNavigate();
  const taskCreationPreference = useSelector(
    (state) => state.user.taskCreationPreference,
  );
  const [showPreferenceDialog, setShowPreferenceDialog] = useState(false);

  const executePreference = useCallback(
    (preference) => {
      if (preference === "guided") {
        openGuidedCreator();
      } else {
        navigate(`/create-task?from=${from}`);
      }
    },
    [from, navigate, openGuidedCreator],
  );

  const triggerCreateTask = useCallback(() => {
    if (!taskCreationPreference) {
      setShowPreferenceDialog(true);
    } else {
      executePreference(taskCreationPreference);
    }
  }, [taskCreationPreference, executePreference]);

  const handlePreferenceSelected = useCallback(
    (preference) => {
      setShowPreferenceDialog(false);
      executePreference(preference);
    },
    [executePreference],
  );

  return {
    triggerCreateTask,
    showPreferenceDialog,
    setShowPreferenceDialog,
    handlePreferenceSelected,
  };
};

export default useCreateTaskAction;

